import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, Send } from "lucide-react";

const BecomeContributor = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    requested_role: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting) return;
    
    // Validate required fields
    if (!formData.full_name || !formData.email || !formData.requested_role) {
      toast({
        title: t("contributor.error"),
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    console.log("Submitting contributor request:", formData);
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from("contributor_requests").insert({
        full_name: formData.full_name,
        email: formData.email,
        requested_role: formData.requested_role,
        message: formData.message,
      });

      console.log("Insert result:", { error });

      if (error) throw error;

      console.log("Request submitted successfully");
      
      // Send confirmation email with current page language
      try {
        const emailResponse = await supabase.functions.invoke('send-contributor-notification', {
          body: {
            email: formData.email,
            name: formData.full_name,
            role: formData.requested_role,
            type: 'submission',
            language: language // Uses current page language (en, ru, or es)
          }
        });
        console.log("Email notification sent:", emailResponse);
      } catch (emailError) {
        console.error("Error sending email notification:", emailError);
        // Don't fail the submission if email fails
      }

      setIsSubmitted(true);
      toast({
        title: t("contributor.success"),
        description: t("contributor.successmessage"),
      });
    } catch (error: any) {
      console.error("Error submitting request:", error);
      toast({
        title: t("contributor.error"),
        description: error.message || t("contributor.errormessage"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-20">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="p-8 text-center">
              <CheckCircle className="w-16 h-16 mx-auto text-primary mb-4" />
              <h1 className="text-3xl font-bold mb-4">{t("contributor.thankyou")}</h1>
              <p className="text-lg text-muted-foreground mb-6">
                {t("contributor.reviewmessage")}
              </p>
              <Button onClick={() => navigate("/")}>{t("contributor.backhome")}</Button>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">{t("contributor.title")}</h1>
            <p className="text-lg text-muted-foreground">{t("contributor.description")}</p>
          </div>

          <Card className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="full_name">{t("contributor.name")} *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  required
                  placeholder={t("contributor.nameplaceholder")}
                />
              </div>

              <div>
                <Label htmlFor="email">{t("contributor.email")} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  placeholder={t("contributor.emailplaceholder")}
                />
              </div>

              <div>
                <Label htmlFor="requested_role">{t("contributor.role")} *</Label>
                <Select
                  value={formData.requested_role}
                  onValueChange={(value) =>
                    setFormData({ ...formData, requested_role: value })
                  }
                  required
                >
                  <SelectTrigger id="requested_role">
                    <SelectValue placeholder={t("contributor.roleplaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blog_editor">
                      {t("contributor.blogeditor")}
                    </SelectItem>
                    <SelectItem value="bible_school_editor">
                      {t("contributor.bibleschooleditor")}
                    </SelectItem>
                    <SelectItem value="both">{t("contributor.both")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message">{t("contributor.message")}</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  rows={5}
                  placeholder={t("contributor.messageplaceholder")}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                <Send className="w-4 h-4 mr-2" />
                {isSubmitting ? t("contributor.submitting") : t("contributor.submit")}
              </Button>
            </form>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BecomeContributor;
